import * as actionTypes from "../constants/actionTypes";
import * as modelHelper from "../helpers/modelHelper";
import * as windowService from "../services/windowService";
import * as rccReducerHelper from "../helpers/rccReducerHelper";

import LoggedUser from "../models/utils/LoggedUser";
import PrivilegeManager from "../models/PrivilegeManager";

let initialPrivilegeManager = new PrivilegeManager(windowService.getSoftwareType(), windowService.getPlatform());

let initialUser = new LoggedUser(0, "");
initialUser.privileges = initialPrivilegeManager.buildPrivileges();

const initialState = {
    loggedUser: initialUser,
    privilege: initialPrivilegeManager,
    loggedIn: false,
    login: null,
    password: null
};

/**
 * Structure of login store
 * - loggedUser {Object}        - object of logged user
 * - privilege {Object}         - stores all data that has any influence on privileges
 * - loggedIn {Boolean}         - if true, user is logged in
 * - login {String}
 * - password {String}
 */
export const loginReducer = (state = initialState, action) => {
    switch (action.type) {
        case actionTypes.SERVER_IS_LICENSED:
        case actionTypes.SERVER_IS_LICENSED_COMPACT:
            let privilegeProduct = state.privilege.updateProduct(
                action.payload.product,
                action.payload.modules,
                action.payload.licenseModules
            );

            return {
                ...state,
                loggedUser: state.loggedUser.updatePrivileges(privilegeProduct.buildPrivileges()),
                privilege: privilegeProduct
            };
        case actionTypes.LOG_IN_SUCCESS:
        case actionTypes.CHANGE_USER_SUCCESS:
            let loggedUser = modelHelper.mapLoggedUserToObject(action.payload.loggedUser);

            let privilegeManager = state.privilege.updateUserPrivileges(
                action.payload.loggedUser.privileges,
                loggedUser.groups,
                action.payload.settingsMenu
            );
            loggedUser.privileges = privilegeManager.buildPrivileges();

            return {
                ...state,
                loggedUser: loggedUser,
                privilege: privilegeManager,
                loggedIn: true,
                login: loggedUser.login,
                password: null
            };
        case actionTypes.LOG_IN_FAILURE:
            return {
                ...state,
                loggedUser: new LoggedUser(0, ""),
                loggedIn: false
            };
        case actionTypes.LOG_IN_SET_LOGIN:
            return {
                ...state,
                login: action.payload.login
            };
        case actionTypes.LOG_IN_SET_PASSWORD:
            return {
                ...state,
                password: action.payload.password
            };
        case actionTypes.INIT_USER_SETTINGS:
        case actionTypes.SAVE_USER_SETTINGS:
            let privilegeUserSettings = state.privilege.updateUserSettings(
                action.payload.dispensingEnabled,
                action.payload.orderingEnabled,
                action.payload.pricingEnabled,
                action.payload.competitiveColorSearch,
                action.payload.tabsEnabled,
                action.payload.spectroEnabled,
                action.payload.printEnabled,
                action.payload.customFormulas,
                action.payload.lightEnabled,
                action.payload.tintometricSystemsEnabled,
                action.payload.qualityControlEnabled,
                action.payload.buildingSite,
                action.payload.lockComputer,
                action.payload.kiosk
            );

            return {
                ...state,
                loggedUser: state.loggedUser.updatePrivileges(privilegeUserSettings.buildPrivileges()),
                privilege: privilegeUserSettings
            };
        case actionTypes.CHANGE_SOFTWARE_TYPE:
            let privilegeSoftwareType = state.privilege.updateSoftwareType(action.payload.type);

            return {
                ...state,
                loggedUser: state.loggedUser.updatePrivileges(privilegeSoftwareType.buildPrivileges()),
                privilege: privilegeSoftwareType
            };
        case actionTypes.SAVE_SERVER_PLUGINS:
            let privilegeServerPlugins = state.privilege.updateDisabledServerPlugins(action.payload.plugins.length > 0);

            return {
                ...state,
                loggedUser: state.loggedUser.updatePrivileges(privilegeServerPlugins.buildPrivileges()),
                privilege: privilegeServerPlugins
            };
        case actionTypes.TMC_AVAILABLE:
            let privilegeTmc = state.privilege.updateTmc(action.payload.available);

            return {
                ...state,
                loggedUser: state.loggedUser.updatePrivileges(privilegeTmc.buildPrivileges()),
                privilege: privilegeTmc
            };
        case actionTypes.SAVE_MACHINE_CONFIGURATION:
        case actionTypes.SAVE_MACHINE_CONFIGURATION_AND_RELOAD_CANISTERS:
            return {
                ...state,
                privilege: state.privilege.updateMachineConfig(action.payload.machineConfigurationList)
            };
        case actionTypes.SAVE_MACHINE_CALLS:
            let privilegeMachineMethods = state.privilege.updateMachineMethods(action.payload.methods);

            return {
                ...state,
                loggedUser: state.loggedUser.updatePrivileges(privilegeMachineMethods.buildPrivileges()),
                privilege: privilegeMachineMethods
            };
        case actionTypes.SAVE_MACHINE_DEVICES:
            let privilegeMachineDevices = state.privilege.updateMachineDevices(action.payload.devices);

            return {
                ...state,
                loggedUser: state.loggedUser.updatePrivileges(privilegeMachineDevices.buildPrivileges()),
                privilege: privilegeMachineDevices
            };
        case actionTypes.INIT_SPECTRO_CONFIGURATION:
        case actionTypes.SAVE_SPECTRO_CONFIGURATION:
            let privilegeSpectroConfig = state.privilege.updateSpectroConfig(
                rccReducerHelper.parseDefaultSpectroType(action.payload.configuration)
            );

            return {
                ...state,
                loggedUser: state.loggedUser.updatePrivileges(privilegeSpectroConfig.buildPrivileges()),
                privilege: privilegeSpectroConfig
            };
        case actionTypes.SAVE_SPECTRO_CALLS:
            let privilegeSpectroMethods = state.privilege.updateSpectroMethods(action.payload.methods);

            return {
                ...state,
                loggedUser: state.loggedUser.updatePrivileges(privilegeSpectroMethods.buildPrivileges()),
                privilege: privilegeSpectroMethods
            };
        case actionTypes.RCC_WEBSOCKET_CLOSED:
            let privilegeRcc = state.privilege.resetRccDependencies();

            return {
                ...state,
                loggedUser: state.loggedUser.updatePrivileges(privilegeRcc.buildPrivileges()),
                privilege: privilegeRcc
            };
        case actionTypes.SETTINGS_SAVE_DISPENSING_ENABLED:
            let privilegeDispensing = state.privilege.updateDisabledDispensing(action.payload.value);

            return {
                ...state,
                loggedUser: state.loggedUser.updatePrivileges(privilegeDispensing.buildPrivileges()),
                privilege: privilegeDispensing
            };
        case actionTypes.SETTINGS_SAVE_ORDERING_ENABLED:
            let privilegeOrdering = state.privilege.updateDisabledOrdering(action.payload.value);

            return {
                ...state,
                loggedUser: state.loggedUser.updatePrivileges(privilegeOrdering.buildPrivileges()),
                privilege: privilegeOrdering
            };
        case actionTypes.SETTINGS_SAVE_PRICING_ENABLED:
            let privilegePricing = state.privilege.updateDisabledPricing(action.payload.value);

            return {
                ...state,
                loggedUser: state.loggedUser.updatePrivileges(privilegePricing.buildPrivileges()),
                privilege: privilegePricing
            };
        case actionTypes.SETTINGS_SAVE_COMPETITIVE_COLOR_SEARCH:
            let privilegeComp = state.privilege.updateDisabledCompetitiveSearch(action.payload.value);

            return {
                ...state,
                loggedUser: state.loggedUser.updatePrivileges(privilegeComp.buildPrivileges()),
                privilege: privilegeComp
            };
        case actionTypes.SETTINGS_SAVE_TABS_ENABLED:
            let privilegeTabs = state.privilege.updateDisabledTabs(action.payload.value);

            return {
                ...state,
                loggedUser: state.loggedUser.updatePrivileges(privilegeTabs.buildPrivileges()),
                privilege: privilegeTabs
            };
        case actionTypes.SETTINGS_SAVE_SPECTRO_ENABLED:
            let privilegeSpectro = state.privilege.updateDisabledSpectro(action.payload.value);

            return {
                ...state,
                loggedUser: state.loggedUser.updatePrivileges(privilegeSpectro.buildPrivileges()),
                privilege: privilegeSpectro
            };
        case actionTypes.SETTINGS_SAVE_PRINT_ENABLED:
            let privilegePrint = state.privilege.updateDisabledPrint(action.payload.value);

            return {
                ...state,
                loggedUser: state.loggedUser.updatePrivileges(privilegePrint.buildPrivileges()),
                privilege: privilegePrint
            };
        case actionTypes.SETTINGS_SAVE_CUSTOM_FORMULAS:
            let privilegeCustomFormulas = state.privilege.updateDisabledCustomFormulas(action.payload.value);

            return {
                ...state,
                loggedUser: state.loggedUser.updatePrivileges(privilegeCustomFormulas.buildPrivileges()),
                privilege: privilegeCustomFormulas
            };
        case actionTypes.SETTINGS_SAVE_REDLIKE_LIGHT_ENABLED:
            let privilegeLight = state.privilege.updateDisabledLight(action.payload.value);

            return {
                ...state,
                loggedUser: state.loggedUser.updatePrivileges(privilegeLight.buildPrivileges()),
                privilege: privilegeLight
            };
        case actionTypes.SETTINGS_SAVE_LOCK_COMPUTER:
            let privilegeComputerLock = state.privilege.updateComputerLock(action.payload.value);

            return {
                ...state,
                loggedUser: state.loggedUser.updatePrivileges(privilegeComputerLock.buildPrivileges()),
                privilege: privilegeComputerLock
            };
        case actionTypes.SETTINGS_SAVE_KIOSK:
            let privilegeKiosk = state.privilege.updateKiosk(action.payload.value);

            return {
                ...state,
                loggedUser: state.loggedUser.updatePrivileges(privilegeKiosk.buildPrivileges()),
                privilege: privilegeKiosk
            };
        case actionTypes.SETTINGS_SAVE_QUALITY_CONTROL_ENABLED:
            let privilegeQualityControl = state.privilege.updateDisabledQualityControl(action.payload.value);

            return {
                ...state,
                loggedUser: state.loggedUser.updatePrivileges(privilegeQualityControl.buildPrivileges()),
                privilege: privilegeQualityControl
            };
        case actionTypes.SETTINGS_SAVE_BUILDING_SITE:
            let privilegeBuildingSite = state.privilege.updateDisabledBuildingSite(action.payload.value);

            return {
                ...state,
                loggedUser: state.loggedUser.updatePrivileges(privilegeBuildingSite.buildPrivileges()),
                privilege: privilegeBuildingSite
            };
        case actionTypes.LOG_OUT_SUCCESS:
        case actionTypes.WEBSOCKET_CLOSED:
            let privilegeLogOut = state.privilege.resetPrivileges();

            return {
                ...initialState,
                loggedUser: state.loggedUser.logOut(privilegeLogOut.buildPrivileges()),
                privilege: privilegeLogOut
            };
        default:
            return state;
    }
};
